// Find & Replace in the Context Panel (M12, D-71): the card's Find mode. The search box, Match case, the count,
// Previous / Next, and the matches by page, each with a tick box (what Replace all changes).
import { useEffect, useRef } from 'react';
import type { FindMatch } from '../../engine/worker/protocol.ts';
import { CloseIcon } from './icons.tsx';

/** A match as the app keeps it: on which page, and whether Replace all should change it. */
export interface FoundMatch extends FindMatch {
  page: number;
  ticked: boolean;
}

export function FindCard(props: {
  width: number;
  query: string;
  setQuery: (q: string) => void;
  matchCase: boolean;
  setMatchCase: (on: boolean) => void;
  matches: FoundMatch[];
  current: number;
  setCurrent: (k: number) => void;
  /** the page being searched (1-based) and how many there are; unset when the search is over */
  searching?: { page: number; of: number };
  /** the search was stopped before the last page */
  stoppedAt?: { page: number; of: number };
  onStop: () => void;
  onTick: (k: number, on: boolean) => void;
  onClose: () => void;
  /** counts up to put the cursor in the search box (Ctrl+F while Find is open) */
  focus: number;
  replacement: string;
  setReplacement: (r: string) => void;
  /** Edit mode (D-74): the Replace box, its buttons and the tick boxes are shown */
  replace: boolean;
  /** the document can be changed now (not view-only, not busy) */
  canReplace: boolean;
  onReplace: () => void;
  onReplaceAll: () => void;
  /** why Replace could not change a match, if it tried */
  failed: (m: FoundMatch) => { why: string; forceable?: boolean } | undefined;
  /** debug mode (D-45): failed matches with a cautious reason can be done anyway */
  debug: boolean;
  onForce: (list: FoundMatch[]) => void;
  /** a question about a replacement (font, room), answered here */
  question?: React.ReactNode;
}) {
  const { matches, current } = props;
  const ticked = matches.filter((m) => m.ticked && (!m.locked || (props.debug && m.lockForceable))).length;
  const forceable = matches.filter((m) => props.failed(m)?.forceable);
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => {
    input.current?.focus();
    input.current?.select();
  }, [props.focus]);
  const step = (d: 1 | -1) => matches.length && props.setCurrent((current + d + matches.length) % matches.length);
  const pages = new Set(matches.map((m) => m.page)).size;
  const plural = (n: number, w: string) => `${n} ${w}${n === 1 ? '' : w.endsWith('h') ? 'es' : 's'}`;
  const count = props.searching
    ? `Searching page ${props.searching.page} of ${props.searching.of}…${matches.length ? ` ${plural(matches.length, 'match')} so far` : ''}`
    : !props.query.trim()
      ? ''
      : matches.length
        ? `${plural(matches.length, 'match')} on ${plural(pages, 'page')}${props.stoppedAt ? ` (stopped at page ${props.stoppedAt.page} of ${props.stoppedAt.of})` : ''}`
        : props.stoppedAt
          ? `No matches up to page ${props.stoppedAt.page} of ${props.stoppedAt.of}`
          : 'No matches';
  return (
    <aside data-testid="find-card" className="card-wrap" style={{ width: props.width }} aria-label="Find">
      <div className="card">
        <div className="card-head">
          <h2>Find</h2>
          <button data-testid="find-close" className="btn icon-btn" aria-label="Close Find" title="Close Find (Esc)" onClick={props.onClose}>
            <CloseIcon size={16} />
          </button>
        </div>
        <div className="find-row">
          <input
            ref={input}
            data-testid="find-input"
            className="find-input"
            type="search"
            placeholder="Find in the document"
            aria-label="Find in the document"
            value={props.query}
            onChange={(e) => props.setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                step(e.shiftKey ? -1 : 1);
              } else if (e.key === 'Escape') {
                e.preventDefault();
                props.onClose();
              }
            }}
          />
          <button data-testid="find-prev" className="btn icon-btn" aria-label="Previous match" title="Previous (Shift+Enter)" disabled={!matches.length} onClick={() => step(-1)}>
            ‹
          </button>
          <button data-testid="find-next" className="btn icon-btn" aria-label="Next match" title="Next (Enter)" disabled={!matches.length} onClick={() => step(1)}>
            ›
          </button>
        </div>
        <div className="find-row">
          <label className="find-option">
            <input data-testid="find-case" type="checkbox" checked={props.matchCase} onChange={(e) => props.setMatchCase(e.target.checked)} />
            Match case
          </label>
          <span data-testid="find-count" className="find-count" aria-live="polite">
            {matches.length > 0 && !props.searching && `${current + 1} / ${matches.length} · `}
            {count}
          </span>
          {props.searching && (
            <button data-testid="find-stop" className="btn" onClick={props.onStop}>
              Stop
            </button>
          )}
        </div>
        {props.replace && (
          <div className="find-row">
            <input
              data-testid="find-replace"
              className="find-input"
              type="text"
              placeholder="Replace with"
              aria-label="Replace with"
              value={props.replacement}
              onChange={(e) => props.setReplacement(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && props.canReplace && matches.length) {
                  e.preventDefault();
                  props.onReplace();
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  props.onClose();
                }
              }}
            />
          </div>
        )}
        {props.replace && !props.question && (
          <div className="find-row">
            <button data-testid="find-replace-one" className="btn" disabled={!props.canReplace || !matches[current] || (!!matches[current].locked && !props.debug)} title="Replace this match, then go to the next (Enter in the Replace box)" onClick={props.onReplace}>
              Replace
            </button>
            <button data-testid="find-skip" className="btn" disabled={matches.length < 2} title="Leave this match, go to the next" onClick={() => step(1)}>
              Skip
            </button>
            <button data-testid="find-replace-all" className="btn btn-primary" disabled={!props.canReplace || !ticked} title="Replace every ticked match (one undo step)" onClick={props.onReplaceAll}>
              Replace all{ticked ? ` (${ticked})` : ''}
            </button>
            {props.debug && forceable.length > 1 && (
              <button data-testid="find-force-all" className="btn" disabled={!props.canReplace} title="Debug mode: replace the failed matches anyway (counted; Export warns)" onClick={() => props.onForce(forceable)}>
                Do it anyway ({forceable.length})
              </button>
            )}
          </div>
        )}
        {props.question && (
          <div data-testid="card-question" className="card-question">
            {props.question}
          </div>
        )}
        {matches.length > 0 && (
          <ol data-testid="find-list" className="find-list">
            {matches.map((m, k) => (
              <li key={`${m.page}-${m.spanIndex}-${m.start}`} data-testid="find-item" data-current={k === current || undefined} className={k === current ? 'is-current' : undefined}>
                {(k === 0 || matches[k - 1].page !== m.page) && <div className="find-page">Page {m.page + 1}</div>}
                <div className="find-item">
                  {props.replace && <input data-testid="find-tick" type="checkbox" checked={m.ticked} disabled={!!m.locked && !(props.debug && m.lockForceable)} aria-label={`Replace this match on page ${m.page + 1}`} title={m.locked ? `Cannot be replaced: ${m.locked}` : 'Replace all changes the ticked matches'} onChange={(e) => props.onTick(k, e.target.checked)} />}
                  <button className="find-snippet" title={m.locked ? `Cannot be replaced: ${m.locked}` : 'Go to this match'} onClick={() => props.setCurrent(k)}>
                    {m.start > 24 ? '…' : ''}
                    {m.text.slice(Math.max(0, m.start - 24), m.start)}
                    <b>{m.text.slice(m.start, m.end)}</b>
                    {m.text.slice(m.end, m.end + 24)}
                    {m.end + 24 < m.text.length ? '…' : ''}
                  </button>
                </div>
                {m.locked && <div className="find-why">Cannot be replaced: {m.locked}</div>}
                {props.failed(m) && (
                  <div data-testid="find-failed" className="find-why is-failed">
                    Could not be replaced: {props.failed(m)!.why}
                    {props.debug && props.failed(m)!.forceable && (
                      <button data-testid="find-force" className="btn" disabled={!props.canReplace} title="Debug mode: replace it anyway (counted; Export warns)" onClick={() => props.onForce([m])}>
                        Do it anyway
                      </button>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ol>
        )}
      </div>
    </aside>
  );
}
